import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useCallback, useMemo, useState } from "react";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import { sharingColumn } from "../../../../layouts/Table/columns/sharingColumn";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import {
  ActionIcon,
  Anchor,
  Avatar,
  CopyButton,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../../layouts/Table/MainTable";
import {
  IconArrowLeft,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconLink,
} from "@tabler/icons-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SelectionSharer from "../../../../components/TableComponents/SelectionSharer";
import {
  getRowClasses,
  minimizedSize,
} from "../../lists/ListingWithContent/listing";
import NoItemsModal from "../../../../components/TableComponents/NoItemsModal";
import {
  deleteUrlShortened,
  editUrlShortened,
  getUrlsShortened,
} from "../../../../api/ConfigsApi";
import CreateUrlButton from "./CreateUrlButton";

const LinksListing = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const { linkId } = useParams();
  const link_id = useMemo(() => parseInt(linkId) || null, [linkId]);

  const columns = useMemo(() => {
    if (link_id) {
      return [
        nameColumn({
          headerName: "Nom",
          disableLink: true,
          handleClick: () => {},
          editFunction: (row) => editUrlShortened,
          width: 290,
          editorStyle: { right: 32 },
          apiRef,
          leftSection: function (row) {
            return (
              <Avatar
                color="initials"
                name={row.name}
                radius={"md"}
                size={36}
                variant="filled"
              />
            );
          },
        }),
        dateColumn(),
      ];
    } else
      return [
        numberColumn(),
        nameColumn({
          editFunction: (row) => editUrlShortened,
          apiRef,
          width: 200,
          editorStyle: { right: 16 },
          handleClick: () => {},
          disableLink: true,
        }),
        {
          field: "url",
          headerName: lang.url_shortener.columns.url,
          width: 200,
          renderCell: ({ value }) => {
            return (
              <Anchor size="md" href={value} target="_blank" truncate="end">
                {value}
              </Anchor>
            );
          },
        },
        {
          field: "shortened_url",
          headerName: lang.url_shortener.columns.shortened_url,
          width: 225,
          renderCell: ({ value }) => {
            return (
              <Group wrap="nowrap" w={"100%"}>
                <Anchor
                  c={"primary"}
                  truncate="end"
                  href={value}
                  target="_blank"
                >
                  {value}
                </Anchor>
                <CopyButton value={value}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={
                        copied ? lang.global.copied : `${lang.global.copy}`
                      }
                      color={copied ? "green" : "black"}
                    >
                      <ActionIcon
                        size={"md"}
                        variant="light"
                        color={copied ? "green" : "primary"}
                        onClick={(e) => {
                          e.stopPropagation();
                          copy();
                        }}
                        ml={"auto"}
                        mr={8}
                      >
                        {copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            );
          },
        },
        // {
        //   field: "url_code",
        //   headerName: lang.url_shortener.columns.url_code,
        //   width: 100,
        //   renderCell: ({ row, value }) => {
        //     return (
        //       <CopyButton value={value}>
        //         {({ copied, copy }) => (
        //           <Tooltip
        //             label={
        //               copied
        //                 ? lang.url_shortener.var_copied
        //                 : "Cliquez pour Copier"
        //             }
        //             color={copied ? "green" : "black"}
        //           >
        //             <Anchor c={"black"} onClick={copy} truncate="end">
        //               {value}
        //             </Anchor>
        //           </Tooltip>
        //         )}
        //       </CopyButton>
        //     );
        //   },
        // },
        numberColumn("nb_clicks", lang.url_shortener.columns.nb_clicks, 120),
        dateColumn(),
        sharingColumn({
          apiRef,
          editFunction: () => editUrlShortened,
        }),
      ];
  }, [link_id, apiRef, lang]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <IconChevronDown
            size={24}
            className="bump"
            opacity={link_id === row.id ? 1 : 0.4}
            style={{
              transform: link_id === row.id ? "rotate(-90deg)" : "none",
              transition: "all ease .3s",
            }}
            onClick={() => handleRowClick({ row })}
          />
        </Group>
      );
    },
    [link_id]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <>
        <SelectionDeleter
          handleDelete={deleteUrlShortened}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />
        <SelectionSharer
          getEditFunction={(row) => editUrlShortened}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />
      </>
    ),
    [apiRef]
  );

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    window.open(row.url, "_blank");
    // const setMinimized = useListStore.getState().setModelMinimized;

    // if (link_id === row.id) {
    //   navigate(`/settings/tools/links/none`);
    // } else {
    //   navigate(`/settings/tools/links/${row.id}`);
    //   setMinimized(true);
    // }
  };

  const handleClose = () => {
    navigate(`/settings/tools/links/none`);
  };

  useListingLoader({
    loading,
    setLoading,
    listGetter: getUrlsShortened,
    responseField: "shortened_urls_list",
    setRows,
  });

  return (
    <Stack flex={1} pr={48} pt={16} gap={0}>
      <Group gap={"md"}>
        <ActionIcon size={"xl"} component={Link} to={"/settings/tools"}>
          <IconArrowLeft />
        </ActionIcon>
        <Text size="xl" fw={600} mr={"auto"}>
          {lang.settings.tool_links}
        </Text>

        <CreateUrlButton />
      </Group>

      <Divider my={16} />

      <Flex flex={1}>
        <Group
          flex={1}
          wrap="nowrap"
          align="stretch"
          gap={0}
          style={{ overflowX: "hidden" }}
          pt={64}
          mt={-64}
        >
          <Flex
            flex={1}
            maw={link_id ? minimizedSize - 48 : null}
            pos={"relative"}
            h={"calc(100vh - 196px)"}
          >
            <NoItemsModal
              rows={rows}
              loading={loading}
              text={
                <Text size="xl" mb={32}>
                  {lang.settings.no_link}
                </Text>
              }
              button={<CreateUrlButton />}
            />
            <MainTable
              apiRef={apiRef}
              columns={columns}
              rows={rows}
              loading={loading}
              setLoading={setLoading}
              id={"model-table"}
              toolbarActions={link_id ? null : toolbarActions}
              actionsCell={actionsCell}
              onRowClick={handleRowClick}
              whiteButtons={!link_id}
              actionsCellWidth={75}
              disableGotoPage={link_id}
              disableVisibleColumns={link_id}
              defaultPerPage={15}
              disablePerPage={link_id}
              disableCheckboxes={!!link_id}
              getRowClassName={(params) =>
                getRowClasses({
                  params,
                  mainId: linkId,
                  integerId: link_id,
                })
              }
              withSorter={!!link_id}
              tableSx={{
                "& .MuiDataGrid-columnHeaders": {
                  border: "none !important",
                  display: link_id ? "none" : "flex",
                },
                "& .MuiDataGrid-cell": {
                  border: "none",
                },
                "& .MuiDataGrid-main": {
                  background: "var(--mantine-color-primary-2)",
                  border: "none !important",
                  borderRadius: 3,
                },
                "& .table-search": {
                  width: link_id ? "200px !important" : "300px !important",
                },
                "& .MuiDataGrid-footerContainer": {
                  border: "none !important",
                },
                "& .table-row": {
                  cursor: "pointer",
                  borderRadius: 3,
                  marginBottom: 1,
                  background: "#fff",
                  border: "1px solid var(--mantine-color-primary-1)",
                },
                "& .MuiDataGrid-pinnedColumnHeaders": {
                  background: "transparent",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left": {
                  background: "var(--mantine-color-primary-2) !important",
                  border: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--right .table-row": {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderLeft: "none !important",
                },
                "& .MuiDataGrid-pinnedColumns--left .table-row": {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  background: "#fff",
                  border: "1px solid   var(--mantine-color-primary-1)",
                  borderRight: "none !important",
                },
                "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
                  borderRadius: link_id ? 3 : 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
                "& .MuiDataGrid-selectedRowCount": {
                  visibility: link_id ? "hidden" : "visible",
                },
                "& .MuiPagination-root": {
                  left: link_id ? 0 : undefined,
                  transform: link_id ? "none" : undefined,
                },
              }}
            />
          </Flex>
        </Group>
      </Flex>
    </Stack>
  );
};

export default LinksListing;
